import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { format, formatDistanceToNow } from 'date-fns';
import ResizableDrawer from '../ResizableDrawer';
import { getOrgHealthDetail } from '../../api/dashboard';
import { formatOrgDisplayName } from '../../utils/display';
import { apiErrorMessage } from '../../utils/healthScore';
import { HEALTH_TIER_STYLES } from './healthTierStyles';
import type { HealthDriver, HealthPillar, HealthPillarBreakdown, OrgHealthDetail, SeatStatus } from '../../types';

/**
 * Why one org's health score is what it is (PEL-2852): the reasons as sentences, each
 * pillar's points, a weekly trend, AI requests by type, and every pro seat's activity
 * now vs the previous window. Everything is computed by the backend, so it reads the same
 * as the `pdash_explain_org_health` MCP tool.
 */

interface Props {
  orgId: string | null;
  onClose: () => void;
  excludedOrgIds: string[];
  excludedUserIds: string[];
}

const PILLAR_LABELS: Record<HealthPillar, string> = {
  activation: 'Activation',
  habit: 'Habit',
  depth: 'Depth',
  reliability: 'Reliability',
};

const PILLAR_COLORS: Record<HealthPillar, string> = {
  activation: '#8b5cf6',
  habit: '#3b82f6',
  depth: '#14b8a6',
  reliability: '#f59e0b',
};

const SEAT_STATUS_STYLES: Record<SeatStatus, { label: string; color: string; bg: string; help: string }> = {
  went_quiet: {
    label: 'Went quiet',
    color: '#f97316',
    bg: 'rgba(249, 115, 22, 0.15)',
    help: 'Active in the previous window, not in this one',
  },
  inactive: { label: 'Inactive', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.12)', help: 'Not active in either window' },
  new: { label: 'New', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)', help: 'Added this window, not active yet' },
  light: { label: 'Light', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)', help: 'Active on only a few days' },
  active: { label: 'Active', color: '#22c55e', bg: 'rgba(34, 197, 94, 0.15)', help: 'Active often enough' },
};

function pct(share: number): string {
  return `${Number((share * 100).toFixed(1))}%`;
}

/** The pillar's input next to its target, in words. */
function actualVsTarget(p: HealthPillarBreakdown): string {
  if (p.actual === null) {
    const why: Record<HealthPillar, string> = {
      activation: 'no pro seats',
      habit: 'no active users',
      depth: 'no active users',
      reliability: 'no AI requests — nothing failed',
    };
    return why[p.pillar];
  }
  switch (p.pillar) {
    case 'activation':
      return `${pct(p.actual)} of seats active · target ${pct(p.target)}`;
    case 'habit':
      return `${p.actual.toFixed(1)} active days per user · target ${p.target}`;
    case 'depth':
      return `${p.actual.toFixed(0)} AI requests per active user · target ${p.target}`;
    case 'reliability':
      return `${pct(p.actual)} failed · scores 0 at ${pct(p.target)}`;
  }
}

function Delta({ now, before, suffix = '', precision = 1 }: { now: number; before: number; suffix?: string; precision?: number }) {
  const diff = Number((now - before).toFixed(precision));
  if (diff === 0) {
    return (
      <Typography component="span" variant="caption" color="text.disabled">
        no change
      </Typography>
    );
  }
  const up = diff > 0;
  const Icon = up ? ArrowDropUpIcon : ArrowDropDownIcon;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', color: up ? '#22c55e' : '#ef4444' }}>
      <Icon sx={{ fontSize: 18, mx: -0.5 }} />
      <Typography component="span" variant="caption" sx={{ fontWeight: 600 }}>
        {Math.abs(diff)}
        {suffix}
      </Typography>
    </Box>
  );
}

function Section({ title, right, children }: { title: string; right?: ReactNode; children: ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.06em', lineHeight: 1.5 }}
        >
          {title}
        </Typography>
        {right}
      </Box>
      {children}
    </Box>
  );
}

function DriverRow({
  driver,
  focused,
  onFocus,
}: {
  driver: HealthDriver;
  focused: boolean;
  onFocus: () => void;
}) {
  const color = driver.pillar ? PILLAR_COLORS[driver.pillar] : '#ef4444';
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.25,
        alignItems: 'flex-start',
        py: 1,
        borderBottom: '1px solid rgba(148, 163, 184, 0.08)',
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, mt: 0.75, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
          {driver.message}
        </Typography>
        {driver.userIds.length > 0 && (
          <Button
            size="small"
            onClick={onFocus}
            sx={{ mt: 0.25, px: 0.5, minWidth: 0, fontSize: '0.7rem', textTransform: 'none' }}
          >
            {focused ? 'Showing these seats below' : `Show ${driver.userIds.length} in the seat list`}
          </Button>
        )}
      </Box>
      {driver.impactPoints !== null && driver.impactPoints > 0 && (
        <Tooltip title={`${PILLAR_LABELS[driver.pillar ?? 'activation']} is ${driver.impactPoints} points short of its maximum`} arrow>
          <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 700, whiteSpace: 'nowrap', mt: 0.25 }}>
            −{driver.impactPoints.toFixed(1)} pts
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
}

function PillarRow({ pillar }: { pillar: HealthPillarBreakdown }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography variant="body2" fontWeight={600} sx={{ width: 90 }}>
          {PILLAR_LABELS[pillar.pillar]}
        </Typography>
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, width: 44 }}>
          {pillar.score.toFixed(0)}
        </Typography>
        <Delta now={pillar.score} before={pillar.previousScore} precision={0} />
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {pillar.points.toFixed(1)} of {pillar.maxPoints.toFixed(1)} pts
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pillar.score}
        sx={{
          height: 6,
          borderRadius: 3,
          my: 0.5,
          bgcolor: 'rgba(148, 163, 184, 0.12)',
          '& .MuiLinearProgress-bar': { bgcolor: PILLAR_COLORS[pillar.pillar], borderRadius: 3 },
        }}
      />
      <Typography variant="caption" color="text.secondary">
        {actualVsTarget(pillar)} · weight {pillar.weight}
      </Typography>
    </Box>
  );
}

export default function OrgHealthDrawer({ orgId, onClose, excludedOrgIds, excludedUserIds }: Props) {
  const [detail, setDetail] = useState<OrgHealthDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [focusedDriver, setFocusedDriver] = useState<number | null>(null);
  // Responses for an org that is no longer the one on screen are dropped: closing A and
  // opening B before A's slow request returns must not paint A's people into B's drawer.
  const latestRequest = useRef(0);

  const load = useCallback(async () => {
    if (!orgId) return;
    const request = ++latestRequest.current;
    setIsLoading(true);
    setError(null);
    try {
      const data = await getOrgHealthDetail(orgId, excludedOrgIds, excludedUserIds);
      if (request === latestRequest.current) setDetail(data);
    } catch (err) {
      console.error('Failed to load org health detail:', err);
      if (request === latestRequest.current) {
        setError(`Couldn't load the breakdown: ${apiErrorMessage(err)}`);
      }
    } finally {
      if (request === latestRequest.current) setIsLoading(false);
    }
  }, [orgId, excludedOrgIds, excludedUserIds]);

  useEffect(() => {
    setDetail(null);
    setFocusedDriver(null);
    if (orgId) {
      load();
    } else {
      // Closing invalidates whatever is still in flight.
      latestRequest.current += 1;
      setIsLoading(false);
    }
  }, [orgId, load]);

  const focusedUserIds = useMemo(() => {
    if (focusedDriver === null || !detail) return null;
    return new Set(detail.drivers[focusedDriver]?.userIds ?? []);
  }, [focusedDriver, detail]);

  const seats = useMemo(() => {
    if (!detail) return [];
    return focusedUserIds ? detail.seats.filter((s) => focusedUserIds.has(s.userId)) : detail.seats;
  }, [detail, focusedUserIds]);

  const weekly = useMemo(
    () => (detail?.weekly ?? []).map((w) => ({ ...w, label: format(new Date(w.weekStart), 'MMM d') })),
    [detail],
  );

  const tier = detail ? HEALTH_TIER_STYLES[detail.org.tier] : null;

  return (
    <ResizableDrawer
      open={orgId !== null}
      onClose={onClose}
      minWidth={640}
      storageKey="pd-org-health-width"
      PaperProps={{ sx: { p: 2.5 } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }} noWrap>
          {detail ? formatOrgDisplayName(detail.org.name) : 'Health score'}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {isLoading && !detail && (
        <Box>
          <Skeleton variant="rounded" height={72} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" height={160} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" height={220} />
        </Box>
      )}

      {detail && tier && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Typography variant="h3" sx={{ fontWeight: 700, color: tier.color, fontVariantNumeric: 'tabular-nums' }}>
              {detail.org.score.toFixed(0)}
            </Typography>
            <Box>
              <Chip
                label={tier.label}
                size="small"
                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, color: tier.color, bgcolor: tier.bg }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <Delta now={detail.org.score} before={detail.previous.score} />
                <Typography variant="caption" color="text.secondary">
                  vs the previous {detail.org.windowDays} days ({detail.previous.score.toFixed(1)},{' '}
                  {HEALTH_TIER_STYLES[detail.previous.tier].label})
                </Typography>
              </Box>
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2.5 }}>
            {detail.org.activeUsers} of {detail.org.paidSeats} pro seats active ·{' '}
            {detail.org.requests.toLocaleString()} AI requests · {format(new Date(detail.windowStart), 'MMM d')} –{' '}
            {format(new Date(detail.windowEnd), 'MMM d, yyyy')}
          </Typography>

          <Section title="Why this score">
            {detail.drivers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nothing is costing this org points, and nothing dropped since the previous window.
              </Typography>
            ) : (
              detail.drivers.map((driver, index) => (
                <DriverRow
                  key={`${driver.kind}-${index}`}
                  driver={driver}
                  focused={focusedDriver === index}
                  onFocus={() => setFocusedDriver((prev) => (prev === index ? null : index))}
                />
              ))
            )}
          </Section>

          <Section title="Pillars">
            {[...detail.pillars]
              .sort((a, b) => b.pointsLost - a.pointsLost)
              .map((pillar) => (
                <PillarRow key={pillar.pillar} pillar={pillar} />
              ))}
          </Section>

          <Section title="Weekly activity" right={<Typography variant="caption" color="text.secondary">rolling 7-day buckets</Typography>}>
            <Box sx={{ height: 190 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={weekly} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
                  <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <YAxis yAxisId="requests" tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                  <YAxis yAxisId="users" orientation="right" tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                  <ChartTooltip
                    contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="requests" dataKey="requests" name="AI requests" fill="#14b8a6" radius={[3, 3, 0, 0]} />
                  <Line yAxisId="users" dataKey="activeUsers" name="Active pro users" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Section>

          {detail.requestTypes.length > 0 && (
            <Section title="AI requests by type">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Last {detail.org.windowDays} days</TableCell>
                    <TableCell align="right">Previous</TableCell>
                    <TableCell align="right">Change</TableCell>
                    <TableCell align="right">Failed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detail.requestTypes.map((t) => (
                    <TableRow key={t.requestType}>
                      <TableCell>{t.label}</TableCell>
                      <TableCell align="right">{t.requests.toLocaleString()}</TableCell>
                      <TableCell align="right">{t.previousRequests.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        <Delta now={t.requests} before={t.previousRequests} precision={0} />
                      </TableCell>
                      <TableCell align="right" sx={{ color: t.failedRequests > 0 ? '#ef4444' : undefined }}>
                        {t.failedRequests}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Section>
          )}

          <Section
            title={`Pro seats (${seats.length}${focusedUserIds ? ` of ${detail.seats.length}` : ''})`}
            right={
              focusedUserIds && (
                <Button size="small" onClick={() => setFocusedDriver(null)} sx={{ textTransform: 'none' }}>
                  Show all seats
                </Button>
              )
            }
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Active days</TableCell>
                  <TableCell align="right">AI requests</TableCell>
                  <TableCell>Last active</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {seats.map((s) => {
                  const status = SEAT_STATUS_STYLES[s.status];
                  return (
                    <TableRow key={s.userId}>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography variant="body2" noWrap>
                          {s.name || s.email || s.userId}
                        </Typography>
                        {s.name && s.email && (
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            {s.email}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={status.help} arrow>
                          <Chip
                            label={status.label}
                            size="small"
                            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, color: status.color, bgcolor: status.bg }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {s.activeDays}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          / {s.previousActiveDays}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {s.requests}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          / {s.previousRequests}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="caption" color="text.secondary">
                          {s.lastActiveAt
                            ? formatDistanceToNow(new Date(s.lastActiveAt), { addSuffix: true })
                            : s.status === 'new'
                              ? 'Not yet'
                              : 'Not in 6 months'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Active days and AI requests show this window / the previous {detail.org.windowDays} days.
            </Typography>
          </Section>
        </>
      )}
    </ResizableDrawer>
  );
}
